import { useContext, useEffect, useMemo, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import NotesRoundedIcon from '@mui/icons-material/NotesRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded'
import { useNls } from '../../../nls/nlsProvider'
import { createMeetingProviderAdapter, meetingStatusLabel, subscribeMeetingMembers, subscribeMeetings, type MeetingMember } from '../../../services/meetingService'
import { useToast } from '../../../context/useToast'
import { useAuth } from '../../../context/useAuth'
import { useTimeline } from '../../../context/useTimeline'
import { ChatContext } from '../../../context/chatContextCore'
import type { MeetingProvider, MeetingRecord } from '../../../types/meetings'
import { MemberActionCard, MemberBottomSheet, MemberEmptyState } from '../controls/MemberControlsPrimitives'
import { MemberNexusHero, MemberSurfaceFrame, type MemberNexusChip } from '../controls/MemberNexusPrimitives'
import { SheetDateField } from '../../Common/SheetDateField'
import { SheetSelect } from '../../Common/SheetSelect'
import { Button } from '../../Common/Button'
import { LinkedGoalPicker, Textarea, TextInput, useLinkedGoalPrefill } from '../../Common'
import { buildMemberMeetingCreateInput, dispatchMeetingPaywall } from './memberMeetingDraft'

type Props = { workspaceId: string; workspaceOwner?: { id: string; name: string }; personalWorkspace?: boolean; canScheduleMeetings?: boolean; nexusChips?: MemberNexusChip[]; onOpenChat?: () => void; onOpenCalendar?: () => void; onOpenHuddle?: () => void; onOpenBoardNode?: (elementId: string) => void }

const meetingDate = (value: MeetingRecord['scheduledAt']): Date => value instanceof Date
  ? value
  : typeof (value as { toDate?: () => Date }).toDate === 'function'
    ? (value as { toDate: () => Date }).toDate()
    : new Date(value as unknown as string)
const dayKey = (date: Date) => date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
const isUpcoming = (meeting: MeetingRecord) => meetingDate(meeting.scheduledAt).getTime() >= Date.now() && !['CANCELLED', 'COMPLETED'].includes(meeting.status)

export const MemberMeetingsScreen = ({ workspaceId, workspaceOwner, personalWorkspace = false, canScheduleMeetings = true, nexusChips = [], onOpenChat, onOpenCalendar, onOpenHuddle, onOpenBoardNode }: Props) => {
  const { t } = useNls()
  const { showToast } = useToast()
  const { isAccessRestricted } = useTimeline()
  const { user } = useAuth()
  const [meetings, setMeetings] = useState<MeetingRecord[]>([])
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingRecord | null>(null)
  const [notesOpen, setNotesOpen] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftNotes, setDraftNotes] = useState('')
  const [draftProvider, setDraftProvider] = useState<MeetingProvider>('GOOGLE_MEET')
  const [draftDate, setDraftDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [draftTime, setDraftTime] = useState('09:00')
  const [draftDuration, setDraftDuration] = useState('30')
  const [members, setMembers] = useState<MeetingMember[]>([])
  const [participantIds, setParticipantIds] = useState<string[]>([])
  const [participantChoice, setParticipantChoice] = useState<string | null>(null)
  const prefillGoalId = useLinkedGoalPrefill()
  const [draftScopeNodeId, setDraftScopeNodeId] = useState<string | null>(prefillGoalId)
  const [saving, setSaving] = useState(false)
  const chatContext = useContext(ChatContext)

  const uid = user?.uid ?? ''

  useEffect(() => subscribeMeetings(workspaceId, setMeetings, () => setMeetings([]), { restricted: !!isAccessRestricted, uid }), [workspaceId, isAccessRestricted, uid])
  useEffect(() => workspaceOwner
    ? subscribeMeetingMembers(workspaceId, workspaceOwner, personalWorkspace, setMembers, () => setMembers([]))
    : undefined, [personalWorkspace, workspaceId, workspaceOwner])

  const upcoming = useMemo(() => meetings.filter(isUpcoming).sort((a, b) => meetingDate(a.scheduledAt).getTime() - meetingDate(b.scheduledAt).getTime()), [meetings])
  const archived = useMemo(() => meetings.filter((meeting) => !isUpcoming(meeting)).sort((a, b) => meetingDate(b.scheduledAt).getTime() - meetingDate(a.scheduledAt).getTime()), [meetings])
  const groupedUpcoming = useMemo(() => {
    const groups = new Map<string, MeetingRecord[]>()
    for (const meeting of upcoming) {
      const key = dayKey(meetingDate(meeting.scheduledAt))
      groups.set(key, [...(groups.get(key) ?? []), meeting])
    }
    return Array.from(groups.entries())
  }, [upcoming])
  const renderMeetingCard = (meeting: MeetingRecord) => (
    <MemberActionCard
      key={meeting.id}
      icon={meeting.status === 'COMPLETED' ? <HistoryRoundedIcon /> : <ScheduleRoundedIcon />}
      title={meeting.title || t('NLSMemberMeetingsUntitled')}
      sub={meetingDate(meeting.scheduledAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
      status={meetingStatusLabel(meeting.status)}
      statusTone={meeting.status === 'LIVE' || meeting.status === 'READY' ? 'success' : meeting.status === 'CANCELLED' ? 'danger' : 'info'}
      onClick={() => { setSelectedMeeting(meeting); setNotesOpen(false) }}
    />
  )
  const hasMeetings = upcoming.length > 0 || archived.length > 0
  const resetDraft = () => {
    setDraftTitle(''); setDraftNotes(''); setDraftProvider('GOOGLE_MEET'); setDraftDate(new Date().toISOString().slice(0, 10)); setDraftTime('09:00'); setDraftDuration('30'); setParticipantIds([]); setParticipantChoice(null); setDraftScopeNodeId(prefillGoalId)
  }
  const addParticipant = (uid: string) => {
    setParticipantIds((current) => current.includes(uid) ? current : [...current, uid])
    setParticipantChoice(null)
  }
  const submitSchedule = async () => {
    if (!canScheduleMeetings) return
    const input = buildMemberMeetingCreateInput({ workspaceId, title: draftTitle, sourceProvider: draftProvider, date: draftDate, time: draftTime, durationMinutes: Number(draftDuration), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, agendaMarkdown: draftNotes, participantIds, scopeNodeId: draftScopeNodeId })
    if (!input) return
    setSaving(true)
    try {
      await createMeetingProviderAdapter(draftProvider).createMeeting(input)
      setScheduleOpen(false)
      resetDraft()
      showToast(t('NLSMemberMeetingsCreated'), 'success')
    } catch (error) {
      if (!dispatchMeetingPaywall(workspaceId, error)) showToast(error instanceof Error ? error.message : t('NLSMemberMeetingsCreateFailed'), 'error')
    } finally {
      setSaving(false)
    }
  }

  return <MemberSurfaceFrame
    className="memberMeetingsMobile"
    testId="member-meetings-screen"
    bottomSlot={canScheduleMeetings ? <Button variant="primary" size="lg" className="memberGradientFab--inline" onClick={() => setScheduleOpen(true)}><AddRoundedIcon fontSize="small" />{t('NLSMemberMeetingsSchedule')}</Button> : undefined}
  >
    <MemberNexusHero
      eyebrow={t('NLSMemberMeetingsEyebrow')}
      title={new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
      chips={[
        { key: 'calendar-busy', label: t('NLSMemberMeetingsBusy'), value: upcoming.length, icon: <GroupsRoundedIcon fontSize="inherit" />, onClick: onOpenCalendar },
        {
          key: 'in-huddle',
          label: t('NLSMemberMeetingsInHuddle'),
          value: chatContext?.huddlePresence ? chatContext.huddlePresence.length : undefined,
          icon: <SensorsRoundedIcon fontSize="inherit" />,
          onClick: onOpenHuddle,
        },
        ...nexusChips,
      ]}
    />
    {hasMeetings ? <>
      {groupedUpcoming.map(([label, list]) => <section key={label} className="memberNexusSection"><div className="memberNexusSection__header"><span>{label}</span><span>{list.length}</span></div><div className="memberNexusCardList">{list.map(renderMeetingCard)}</div></section>)}
      {archived.length > 0 ? <section className="memberNexusSection"><div className="memberNexusSection__header"><span>{t('NLSMemberMeetingsPast')}</span><span>{archived.length}</span></div><div className="memberNexusCardList">{archived.map(renderMeetingCard)}<p className="memberMeetingsEnd">{t('NLSMemberMeetingsEnd', { count: archived.length })}</p></div></section> : null}
    </> : <MemberEmptyState icon={<ScheduleRoundedIcon />} title={t('NLSMemberMeetingsEmptyTitle')} hint={t('NLSMemberMeetingsEmptyHint')} />}

    <MemberBottomSheet open={Boolean(selectedMeeting)} onClose={() => { setSelectedMeeting(null); setNotesOpen(false) }} title={selectedMeeting?.title ?? t('NLSMemberMeetingsTitle')}>
      {selectedMeeting ? (
        <div className="memberNexusSheet">
          <MemberActionCard
            icon={<NotesRoundedIcon />}
            title={selectedMeeting.title}
            sub={selectedMeeting.agendaMarkdown || selectedMeeting.description || t('NLSMemberMeetingsNoNotes')}
            status={meetingStatusLabel(selectedMeeting.status)}
            statusTone={selectedMeeting.status === 'CANCELLED' ? 'danger' : 'info'}
          />
          {notesOpen ? (
            <div className="memberMeetingNotesPanel" data-testid="member-meeting-notes-panel">
              {selectedMeeting.agendaMarkdown ? <p className="memberMeetingNotesText">{selectedMeeting.agendaMarkdown}</p> : null}
              {selectedMeeting.agendaNodes && selectedMeeting.agendaNodes.length > 0 ? (
                <ul className="memberMeetingAgendaNodes">
                  {selectedMeeting.agendaNodes.map((node) => (
                    <li key={node.nodeId}>{node.title}</li>
                  ))}
                </ul>
              ) : null}
              {!selectedMeeting.agendaMarkdown && (!selectedMeeting.agendaNodes || selectedMeeting.agendaNodes.length === 0) ? (
                <p className="memberMeetingNotesEmpty">{selectedMeeting.description || t('NLSMemberMeetingsNoNotes')}</p>
              ) : null}
            </div>
          ) : null}
          <div className="memberNexusSheet__actions">
            <Button
              variant="secondary"
              size="md"
              onClick={() => setNotesOpen((prev) => !prev)}
              aria-expanded={notesOpen}
            >
              {notesOpen ? t('NLSMemberMeetingsHideNotes') : t('NLSMemberMeetingsOpenNotes')}
            </Button>
            <Button variant="secondary" size="md" onClick={onOpenChat}>{t('NLSMemberMeetingsMessageChannel')}</Button>
            <Button variant="secondary" size="md" onClick={() => selectedMeeting.agendaNodes?.[0]?.nodeId ? onOpenBoardNode?.(selectedMeeting.agendaNodes[0].nodeId) : showToast(t('NLSMemberMeetingsNoBoardLink'), 'ghost')}>{t('NLSMemberMeetingsOpenBoard')}</Button>
          </div>
        </div>
      ) : null}
    </MemberBottomSheet>

    <MemberBottomSheet open={scheduleOpen} onClose={() => setScheduleOpen(false)} title={t('NLSMemberMeetingsScheduleTitle')}>
      <div className="memberNexusSheet memberMeetingDraftSheet">
        <label><span>{t('NLSMemberMeetingsFieldTitle')}</span><TextInput block className="memberTextOverlayField" value={draftTitle} onChange={setDraftTitle} placeholder={t('NLSMemberMeetingsTitlePlaceholder')} /></label>
        <label><span>{t('NLSMemberMeetingsType')}</span><SheetSelect ariaLabel={t('NLSMemberMeetingsType')} value={draftProvider} options={[{ value: 'GOOGLE_MEET', label: t('NLSMemberMeetingsTypeMeeting') }, { value: 'HUDDLE', label: t('NLSMemberMeetingsTypeHuddle') }]} onChange={setDraftProvider} /></label>
        <label><span>{t('NLSMemberMeetingsDate')}</span><SheetDateField ariaLabel={t('NLSMemberMeetingsDate')} value={draftDate} type="date" placeholder={t('NLSMemberMeetingsDate')} onChange={setDraftDate} /></label>
        <label><span>{t('NLSMemberMeetingsTime')}</span><SheetDateField ariaLabel={t('NLSMemberMeetingsTime')} value={draftTime} type="time" placeholder={t('NLSMemberMeetingsTime')} onChange={setDraftTime} /></label>
        <label><span>{t('NLSMemberMeetingsDuration')}</span><SheetSelect ariaLabel={t('NLSMemberMeetingsDuration')} value={draftDuration} options={['15', '30', '45', '60', '90', '120', '10080'].map((value) => ({ value, label: t('NLSMemberMeetingsDurationMinutes', { minutes: Number(value) }) }))} onChange={setDraftDuration} /></label>
        {members.length > 0 ? <label><span>{t('NLSMemberMeetingsParticipants')}</span><SheetSelect ariaLabel={t('NLSMemberMeetingsParticipants')} value={participantChoice} placeholder={t('NLSMemberMeetingsAddParticipant')} options={members.filter((member) => !participantIds.includes(member.uid)).map((member) => ({ value: member.uid, label: member.name }))} onChange={addParticipant} /></label> : null}
        {participantIds.length > 0 ? <div className="memberNexusSheet__actions">{participantIds.map((uid) => <Button key={uid} variant="secondary" size="md" onClick={() => setParticipantIds((current) => current.filter((id) => id !== uid))}>{t('NLSMemberMeetingsRemoveParticipant', { name: members.find((member) => member.uid === uid)?.name ?? uid })}</Button>)}</div> : null}
        <label><span>{t('NLSMemberMeetingsFieldNotes')}</span><Textarea block className="memberTextOverlayField" value={draftNotes} onChange={setDraftNotes} placeholder={t('NLSMemberMeetingsNotesPlaceholder')} /></label>
        <LinkedGoalPicker value={draftScopeNodeId} onChange={setDraftScopeNodeId} disabled={saving} />
        <Button variant="primary" size="lg" disabled={!draftTitle.trim() || saving} onClick={() => void submitSchedule()}>{saving ? t('NLSMemberMeetingsCreating') : t('NLSMemberMeetingsSchedule')}</Button>
      </div>
    </MemberBottomSheet>
  </MemberSurfaceFrame>
}
